// Decides whether the Prismic toolbar (edit button, preview sharing) loads.
// It is off for everyone by default (MKTM-1764); the plugin's SSR copy is
// stripped in gatsby-ssr.tsx and we load it client-side only when needed.

export const TOOLBAR_COOKIE = "xom_prismic_toolbar";
export const TOOLBAR_PARAM = "prismic-toolbar";
const PRISMIC_PREVIEW_COOKIE = "io.prismic.preview";
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;
const STAFF_EMAIL_SUFFIX = "@xometry.com";

const readCookie = (cookie: string, name: string): string | undefined => {
  for (const part of cookie.split(";")) {
    const [key, ...rest] = part.trim().split("=");
    if (key === name) return rest.join("=");
  }
  return undefined;
};

export interface PrismicToolbarContext {
  pathname: string;
  cookie: string;
  userEmail?: string;
}

export function shouldLoadPrismicToolbar(
  { pathname, cookie, userEmail }: PrismicToolbarContext,
  crawler: boolean
): boolean {
  if (crawler) return false;

  // Preview sessions always get the toolbar; the preview flow depends on it.
  if (
    pathname.startsWith("/preview") ||
    readCookie(cookie, PRISMIC_PREVIEW_COOKIE) !== undefined
  ) {
    return true;
  }

  // An explicit choice beats the automatic staff rule.
  const choice = readCookie(cookie, TOOLBAR_COOKIE);
  if (choice === "0") return false;
  if (choice === "1") return true;

  return !!userEmail?.toLowerCase().endsWith(STAFF_EMAIL_SUFFIX);
}

// Maps `?prismic-toolbar=1|0|reset` to the Set-Cookie string to write, or null
// when the param is absent or unrecognized.
export function toolbarCookieFromSearch(
  search: string,
  secure: boolean
): string | null {
  const value = new URLSearchParams(search).get(TOOLBAR_PARAM);
  const attrs = `path=/; SameSite=Lax${secure ? "; Secure" : ""}`;
  if (value === "1" || value === "0") {
    return `${TOOLBAR_COOKIE}=${value}; max-age=${ONE_YEAR_SECONDS}; ${attrs}`;
  }
  if (value === "reset") {
    return `${TOOLBAR_COOKIE}=; max-age=0; ${attrs}`;
  }
  return null;
}

// Browser-only: persists the editor's choice from the current URL.
export function applyToolbarQueryParam(): void {
  if (typeof window === "undefined") return;
  const cookie = toolbarCookieFromSearch(
    window.location.search,
    window.location.protocol === "https:"
  );
  if (cookie) document.cookie = cookie;
}

// Client-side navigation does not remount the toolbar gate, so route changes
// announce themselves and the gate re-evaluates its rule (path, cookies).
export const TOOLBAR_RECHECK_EVENT = "xom:prismic-toolbar-recheck";

export function requestToolbarRecheck(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(TOOLBAR_RECHECK_EVENT));
}
